import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Flex, Layout, Tag, Typography } from 'antd';
import { useParams } from 'react-router-dom';

import useLPPartner, { PartnerData } from '../api/useLPPartner';
import LPPartnerDetailsContent from '../components/LPPartnerDetailsContent';
import { usePartnerClosureRequest } from '@/features/gdpr/partnerClosure/api/getPartnerClosureRequest';
import { useGetPartnersActivity } from '@/features/gdpr/partnerClosure/api/getPartnersActivity';
import { usePartnerStats } from '@/features/gdpr/partnerClosure/api/getPartnerStats';
import { ClosureRequestStatusTag } from '@/features/gdpr/partnerClosure/components/ClosureRequestStatusTag';
import { closureRequestStatuses } from '@/features/gdpr/partnerClosure/types';

const { Title } = Typography;
const { Content } = Layout;

export const CurrentPartnerContext = React.createContext<PartnerData>(
  {} as PartnerData
);

const LPPartnerDetails = () => {
  const { id } = useParams<{ id: string }>();

  const partnerData = useLPPartner(id!);

  // TODO: récupérer le nom de l'application OIDC sur Auth0 depuis l'API
  const isSso = partnerData.partnerState.data.sso_enabled;

  const { data: closureRequest } = usePartnerClosureRequest();
  const { data, isPending } = usePartnerStats({});
  const hasUnpaidBills = (data?.unpaidBills?.value ?? 0) > 0;
  const { data: isPartnerActive, isLoading } = useGetPartnersActivity({
    partnerIds: [id!],
    config: {
      enabled: !partnerData.partnerState.loading,
    },
  });

  // Doing this to avoid flickering the tag when loading the partner comes after the unpaidBill query
  const isPartnerInactive = isLoading ? false : !(isPartnerActive as boolean);
  const isClosureRequestedApproved =
    closureRequest?.status === closureRequestStatuses.Approved;
  const isClosureRequestedAnonymized =
    closureRequest?.status === closureRequestStatuses.Anonymized;

  return (
    <Layout style={{ padding: '2rem' }}>
      <Flex
        gap="1rem"
        style={{
          alignItems: 'center',
        }}
      >
        <Title style={{ marginBottom: '0' }}>
          {partnerData.partnerState.data.partner_id_card.client_name}
        </Title>
        {!isPending && (
          // Tags Section
          <Flex>
            <ClosureRequestStatusTag closureRequest={closureRequest} />
            {isPartnerInactive &&
              !isClosureRequestedApproved &&
              !isClosureRequestedAnonymized && (
                <Tag
                  data-testid="tag-inactivity"
                  icon={<ExclamationCircleOutlined />}
                  color="red"
                >
                  Inactivity
                </Tag>
              )}
            {isSso && (
              <Tag color="blue" style={{ width: 'fit-content' }}>
                SSO Partner{' '}
                {partnerData.partnerState.data.sso_connection_name ? (
                  <strong>
                    ({partnerData.partnerState.data.sso_connection_name})
                  </strong>
                ) : (
                  ''
                )}
              </Tag>
            )}
            {hasUnpaidBills && (
              <Tag
                data-testid="tag-unpaid-bills"
                icon={<ExclamationCircleOutlined />}
                color="red"
              >
                Unpaid bills
              </Tag>
            )}
          </Flex>
        )}
      </Flex>

      <CurrentPartnerContext.Provider value={partnerData}>
        <Content>
          <LPPartnerDetailsContent isPartnerInactive={isPartnerInactive} />
        </Content>
      </CurrentPartnerContext.Provider>
    </Layout>
  );
};

export default LPPartnerDetails;
